import { getPosition as sunGetPosition, getTimes as sunGetTimes } from 'suncalc'

export type SunPosition = {
  azimuthDeg: number
  altitudeDeg: number
  azimuthRad: number
  altitudeRad: number
}

export type SunTimes = {
  sunrise: Date | null
  sunset: Date | null
  solarNoon: Date | null
  dawn: Date | null
  dusk: Date | null
  nauticalDawn: Date | null
  nauticalDusk: Date | null
  nightEnd: Date | null
  night: Date | null
  goldenHour: Date | null
  goldenHourEnd: Date | null
}

function toDate(v: Date | number | undefined | null): Date | null {
  if (v == null) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * SunCalc v2: azimuth is already compass degrees (0=N, clockwise),
 * altitude is already degrees above horizon.
 */
export function sunPosition(date: Date, lat: number, lng: number): SunPosition {
  const pos = sunGetPosition(date, lat, lng)
  const altitudeDeg = Math.max(-90, Math.min(90, pos.altitude))
  const azimuthDeg = ((pos.azimuth % 360) + 360) % 360
  return {
    azimuthDeg,
    altitudeDeg,
    azimuthRad: (azimuthDeg * Math.PI) / 180,
    altitudeRad: (altitudeDeg * Math.PI) / 180,
  }
}

export function getSunTimes(lat: number, lng: number, date: Date): SunTimes {
  const t = sunGetTimes(date, lat, lng)
  return {
    sunrise: toDate(t.sunrise),
    sunset: toDate(t.sunset),
    solarNoon: toDate(t.solarNoon),
    dawn: toDate(t.dawn),
    dusk: toDate(t.dusk),
    nauticalDawn: toDate(t.nauticalDawn),
    nauticalDusk: toDate(t.nauticalDusk),
    nightEnd: toDate(t.nightEnd),
    night: toDate(t.night),
    goldenHour: toDate(t.goldenHour),
    goldenHourEnd: toDate(t.goldenHourEnd),
  }
}

export function formatTime(d: Date | null): string {
  if (!d) return '—'
  return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
}

export type LightRelation = 'backlight' | 'frontlight' | 'sidelight'

/** Camera bearing = direction the lens looks (0 north). */
export function lightRelation(cameraBearing: number, sunAzimuth: number): LightRelation {
  const towardSun = Math.abs(((sunAzimuth - cameraBearing + 540) % 360) - 180)
  if (towardSun <= 45) return 'backlight'
  if (towardSun >= 135) return 'frontlight'
  return 'sidelight'
}

export function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes()
}

export function setMinutesOfDay(base: Date, minutes: number): Date {
  const next = new Date(base)
  const clamped = Math.max(0, Math.min(24 * 60 - 1, minutes))
  next.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0)
  return next
}

export function dayPhaseLabel(date: Date, lat: number, lng: number): string {
  const t = getSunTimes(lat, lng, date)
  const ms = date.getTime()
  const between = (a: Date | null, b: Date | null) =>
    !!(a && b && ms >= a.getTime() && ms <= b.getTime())

  if (t.nightEnd && t.dawn && between(t.nightEnd, t.dawn)) return 'Noc → świt astronomiczny'
  if (between(t.dawn, t.sunrise)) return 'Blue hour (poranek)'
  if (t.sunrise && t.goldenHourEnd && between(t.sunrise, t.goldenHourEnd))
    return 'Golden hour (poranek)'
  if (t.goldenHourEnd && t.goldenHour && between(t.goldenHourEnd, t.goldenHour)) return 'Dzień'
  if (between(t.goldenHour, t.sunset)) return 'Golden hour (wieczór)'
  if (between(t.sunset, t.dusk)) return 'Blue hour (wieczór)'
  if (t.dusk && t.night && between(t.dusk, t.night)) return 'Zmierzch'
  return 'Noc'
}
